import React, { useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import { FacetFilter } from '../../../@types/Search';
import { Facet } from '../SearchModels';
import OverflowBox from '../../../StyledComponents/OverflowBox';
import { RepeatSmall } from '../../../StyledComponents/Repeat';
import InputCheckbox from '../../../StyledComponents/InputCheckbox';
import { resolveBranchName } from '../../../utils/resolver';
import { Columns, Column } from '../../../StyledComponents/Columns';
import { Text } from '../../../StyledComponents/Text';
import { ButtonInline } from '../../../StyledComponents/Button';

interface CheckListFilterProps {
  facet: Facet;
  loading?: boolean;
  onFilterUpdate: (query: string) => void;
}

const CheckListFilter = ({ facet, loading, onFilterUpdate }: CheckListFilterProps) => {
  const [selected, setSelected] = useState<{ [key: string]: boolean }>();
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!loading) {
      setSelected(undefined);
    }
  }, [loading]);

  const onClick = useCallback((f: FacetFilter) => {
    setSelected((s) => ({
      ...(s || {}),
      [f.displayName]: !f.selected,
    }));
    onFilterUpdate(f.query);
  }, []);

  const resolveName = (f: FacetFilter, id: string) => {
    if (id !== 'branch') return f.displayName;
    return resolveBranchName(f.displayName) as string;
  };

  const filters = showAll ? facet.filters : facet.filters.filter(({ count }) => count > 0);

  return (
    <OverflowBox>
      {filters
        .sort((a, b) => b.count - a.count)
        .map((f, i) => (
          <RepeatSmall key={`${resolveName(f, facet.id)}-${i}`}>
            <InputCheckbox
              id={`${resolveName(f, facet.id)}-${i}`}
              onChange={() => onClick(f)}
              checked={
                selected?.[f.displayName] !== undefined ? selected[f.displayName] : f.selected
              }
            >
              <Columns $spacing={2} align="center">
                <Column grow>{resolveName(f, facet.id)}</Column>
                <Column noShrink>
                  <Text $tone="alt" $size="small">
                    {f.count}
                  </Text>
                </Column>
              </Columns>
            </InputCheckbox>
          </RepeatSmall>
        ))}
      {!showAll && filters.length !== facet.filters.length && (
        <RepeatSmall>
          <ButtonInline onClick={() => setShowAll(true)}>
            {i18next.t('common.showAll')}
          </ButtonInline>
        </RepeatSmall>
      )}
    </OverflowBox>
  );
};

export default CheckListFilter;
